/**
 * Toast Employees View - Pure Vanilla JS
 * Simple list display of employee data from Toast POS
 * With App Password management for employee portal access
 *
 * Usage:
 *   import ToastEmployeesView from './ToastEmployeesView';
 *   const view = ToastEmployeesView.init(containerEl, { employees, jobs, loading, getAuthToken, apiHelpers });
 *   view.update({ employees, jobs, loading });
 *   view.destroy();
 */

// OWASP XSS Prevention - HTML escape function
const escapeHtml = (str) => {
  if (str === null || str === undefined) return '';
  const div = document.createElement('div');
  div.textContent = String(str);
  return div.innerHTML;
};

const ToastEmployeesView = {
  init(container, options) {
    const { employees = [], jobs = [], shifts = [], loading = false, getAuthToken, apiHelpers } = options;

    // Instance state
    const instance = {
      container,
      employees,
      jobs,
      shifts,
      loading,
      getAuthToken,
      apiHelpers,
      state: {
        searchTerm: '',
        jobFilter: '',
        statusFilter: 'active',
        sortColumn: 'name',
        sortDirection: 'asc',
        expandedRow: null,
        appCredentials: {},
        generatedCreds: null,
        credLoading: false,
        credError: null,
      },
      boundHandleClick: null,
      boundHandleInput: null,
      boundHandleChange: null,
    };

    // Bind event handlers
    instance.boundHandleClick = (e) => handleClick(e, instance);
    instance.boundHandleInput = (e) => handleInput(e, instance);
    instance.boundHandleChange = (e) => handleChange(e, instance);

    // Attach event listeners
    container.addEventListener('click', instance.boundHandleClick);
    container.addEventListener('input', instance.boundHandleInput);
    container.addEventListener('change', instance.boundHandleChange);

    // Initial render
    render(instance);
    fetchAllCredentials(instance);

    // Return public interface
    return {
      update(newOptions) {
        if (newOptions.employees !== undefined) instance.employees = newOptions.employees;
        if (newOptions.jobs !== undefined) instance.jobs = newOptions.jobs;
        if (newOptions.shifts !== undefined) instance.shifts = newOptions.shifts;
        if (newOptions.loading !== undefined) instance.loading = newOptions.loading;
        render(instance);
      },
      destroy() {
        container.removeEventListener('click', instance.boundHandleClick);
        container.removeEventListener('input', instance.boundHandleInput);
        container.removeEventListener('change', instance.boundHandleChange);
        container.innerHTML = '';
      },
      getState() {
        return { ...instance.state };
      }
    };
  }
};

// API Functions
async function fetchAllCredentials(instance) {
  try {
    const token = await instance.getAuthToken();
    const response = await instance.apiHelpers.get({
      apiName: 'ChulasEmployeeAPI',
      path: '/v1/admin/scheduling/app-credentials',
      options: { headers: { Authorization: token } }
    }).response;
    const data = await response.body.json();
    const credsMap = {};
    (data.credentials || []).forEach(cred => {
      credsMap[cred.toast_employee_guid] = cred;
    });
    instance.state.appCredentials = credsMap;
    render(instance);
  } catch (err) {
    console.error('Error fetching credentials:', err);
  }
}

async function generateAppCredentials(instance, employee) {
  instance.state.credLoading = true;
  instance.state.credError = null;
  instance.state.generatedCreds = null;
  render(instance);

  try {
    const token = await instance.getAuthToken();
    const response = await instance.apiHelpers.post({
      apiName: 'ChulasEmployeeAPI',
      path: '/v1/admin/scheduling/app-credentials',
      options: {
        headers: { Authorization: token },
        body: {
          toast_employee_guid: employee.guid,
          first_name: employee.firstName,
          last_name: employee.lastName,
          employee_name: `${employee.firstName} ${employee.lastName}`.trim()
        }
      }
    }).response;
    const data = await response.body.json();
    instance.state.generatedCreds = data;
    instance.state.appCredentials[employee.guid] = data;
  } catch (err) {
    const errorData = err.response?.body ? await err.response.body.json() : null;
    instance.state.credError = errorData?.error || 'Failed to generate credentials';
  } finally {
    instance.state.credLoading = false;
    render(instance);
  }
}

async function resetAppPassword(instance, toastGuid) {
  instance.state.credLoading = true;
  instance.state.credError = null;
  instance.state.generatedCreds = null;
  render(instance);

  try {
    const token = await instance.getAuthToken();
    const response = await instance.apiHelpers.put({
      apiName: 'ChulasEmployeeAPI',
      path: `/v1/admin/scheduling/app-credentials/${toastGuid}/reset`,
      options: { headers: { Authorization: token } }
    }).response;
    const data = await response.body.json();
    instance.state.generatedCreds = data;
  } catch (err) {
    instance.state.credError = 'Failed to reset password';
  } finally {
    instance.state.credLoading = false;
    render(instance);
  }
}

// Helper Functions
function getJobName(instance, jobGuid) {
  const job = instance.jobs.find(j => j.guid === jobGuid);
  return job?.title || job?.name || 'Unknown';
}

function formatPhone(phone) {
  if (!phone) return '—';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
}

function formatWage(wage) {
  if (!wage) return '—';
  return `$${parseFloat(wage).toFixed(2)}/hr`;
}

function formatDate(dateString) {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getFilteredEmployees(instance) {
  const { state, employees } = instance;
  let filtered = [...employees];

  // Status filter
  if (state.statusFilter === 'active') {
    filtered = filtered.filter(e => !e.deleted && !e.disabled);
  } else if (state.statusFilter === 'inactive') {
    filtered = filtered.filter(e => e.deleted || e.disabled);
  }

  // Search filter
  if (state.searchTerm) {
    const term = state.searchTerm.toLowerCase();
    filtered = filtered.filter(e => {
      const name = `${e.firstName || ''} ${e.lastName || ''}`.toLowerCase();
      const email = (e.email || '').toLowerCase();
      const phone = (e.phoneNumber || '').toLowerCase();
      const positions = e.jobReferences?.map(jr => getJobName(instance, jr.guid).toLowerCase()).join(' ') || '';
      const employeeId = (e.guid || '').toLowerCase();
      return name.includes(term) || email.includes(term) || phone.includes(term) || positions.includes(term) || employeeId.includes(term);
    });
  }

  // Job filter
  if (state.jobFilter) {
    filtered = filtered.filter(e => e.jobReferences?.some(jr => jr.guid === state.jobFilter));
  }

  // Sort
  filtered.sort((a, b) => {
    let valueA, valueB;
    switch (state.sortColumn) {
      case 'name':
        valueA = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
        valueB = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
        break;
      case 'position':
        valueA = a.jobReferences?.map(jr => getJobName(instance, jr.guid)).join(', ') || '';
        valueB = b.jobReferences?.map(jr => getJobName(instance, jr.guid)).join(', ') || '';
        break;
      case 'phone':
        valueA = a.phoneNumber || '';
        valueB = b.phoneNumber || '';
        break;
      case 'status':
        valueA = a.deleted || a.disabled ? 1 : 0;
        valueB = b.deleted || b.disabled ? 1 : 0;
        break;
      case 'employeeId':
        valueA = (a.guid || '').toLowerCase();
        valueB = (b.guid || '').toLowerCase();
        break;
      default:
        valueA = '';
        valueB = '';
    }
    if (typeof valueA === 'string') {
      const result = valueA.localeCompare(valueB);
      return state.sortDirection === 'asc' ? result : -result;
    }
    const result = valueA - valueB;
    return state.sortDirection === 'asc' ? result : -result;
  });

  return filtered;
}

// Render function
function render(instance) {
  const { container, jobs, state } = instance;
  if (!container) return;

  const filteredEmployees = getFilteredEmployees(instance);

  // Build job options HTML
  const jobOptionsHtml = jobs.map(job =>
    `<option value="${escapeHtml(job.guid)}" ${state.jobFilter === job.guid ? 'selected' : ''}>${escapeHtml(job.title || job.name)}</option>`
  ).join('');

  // Build table rows HTML
  const tableRowsHtml = filteredEmployees.map(employee => {
    const isInactive = employee.deleted || employee.disabled;
    const hasAppAccess = state.appCredentials[employee.guid];
    const positions = employee.jobReferences?.length > 0
      ? employee.jobReferences.map(jr => escapeHtml(getJobName(instance, jr.guid))).join(', ')
      : '—';

    return `
      <tr class="${isInactive ? 'inactive' : ''}" data-guid="${escapeHtml(employee.guid)}">
        <td class="col-id">${escapeHtml(employee.guid?.slice(0, 8) || '—')}</td>
        <td class="col-name">${escapeHtml(employee.firstName)} ${escapeHtml(employee.lastName)}</td>
        <td>${positions}</td>
        <td>${escapeHtml(formatPhone(employee.phoneNumber))}</td>
        <td>
          <div class="status-cell">
            <span class="status-badge ${isInactive ? 'inactive' : 'active'}">
              ${isInactive ? 'Inactive' : 'Active'}
            </span>
            ${hasAppAccess ? `
              <span class="app-user-badge" title="Has portal access">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </span>
            ` : ''}
          </div>
        </td>
        <td class="col-actions">
          <div class="info-wrapper">
            <button class="info-btn ${state.expandedRow === employee.guid ? 'active' : ''}" data-action="toggle-info" data-guid="${escapeHtml(employee.guid)}" title="More info">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="16" x2="12" y2="12"/>
                <line x1="12" y1="8" x2="12.01" y2="8"/>
              </svg>
            </button>
            ${state.expandedRow === employee.guid ? renderPopover(instance, employee) : ''}
          </div>
        </td>
      </tr>
    `;
  }).join('');

  // Sort arrow SVG
  const getSortArrow = (column) => {
    if (state.sortColumn !== column) return '';
    return `<span class="sort-arrow"><svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
      ${state.sortDirection === 'asc' ? '<path d="M5 2L9 7H1L5 2Z"/>' : '<path d="M5 8L1 3H9L5 8Z"/>'}
    </svg></span>`;
  };

  container.innerHTML = `
    <div class="toast-list-container">
      <div class="list-toolbar">
        <div class="search-box">
          <input type="text" placeholder="Search by ID, name, email, phone, or position..." value="${escapeHtml(state.searchTerm)}" data-action="search" />
          ${state.searchTerm ? '<button class="clear-search" data-action="clear-search">×</button>' : ''}
        </div>
        <select class="filter-select" data-action="job-filter">
          <option value="">All Positions</option>
          ${jobOptionsHtml}
        </select>
        <select class="filter-select" data-action="status-filter">
          <option value="active" ${state.statusFilter === 'active' ? 'selected' : ''}>Active</option>
          <option value="inactive" ${state.statusFilter === 'inactive' ? 'selected' : ''}>Inactive</option>
          <option value="all" ${state.statusFilter === 'all' ? 'selected' : ''}>All</option>
        </select>
        <span class="results-count">${filteredEmployees.length} employees</span>
      </div>

      ${filteredEmployees.length === 0 ? `
        <div class="empty-state">
          <h3>No employees found</h3>
          <p>${state.searchTerm || state.jobFilter ? 'Try adjusting your search or filters' : 'Employee data will appear here once synced from Toast'}</p>
        </div>
      ` : `
        <table class="data-table">
          <thead>
            <tr>
              <th class="sortable" data-action="sort" data-column="employeeId">ID ${getSortArrow('employeeId')}</th>
              <th class="sortable" data-action="sort" data-column="name">Name ${getSortArrow('name')}</th>
              <th class="sortable" data-action="sort" data-column="position">Position ${getSortArrow('position')}</th>
              <th class="sortable" data-action="sort" data-column="phone">Phone ${getSortArrow('phone')}</th>
              <th class="sortable" data-action="sort" data-column="status">Status ${getSortArrow('status')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            ${tableRowsHtml}
          </tbody>
        </table>
      `}
    </div>
  `;
}

function renderPopover(instance, employee) {
  const { state } = instance;
  const creds = state.appCredentials[employee.guid];

  return `
    <div class="info-popup-overlay" data-action="close-popup"></div>
    <div class="info-popup">
      <div class="popup-header">
        <span>${escapeHtml(employee.firstName)} ${escapeHtml(employee.lastName)}</span>
        <button class="popup-close" data-action="close-popup">×</button>
      </div>
      <div class="popup-content">
        <div class="popup-row">
          <span class="popup-label">Email</span>
          <span class="popup-value">${escapeHtml(employee.email || '—')}</span>
        </div>
        <div class="popup-row">
          <span class="popup-label">Pay Rate</span>
          <span class="popup-value popup-pay">${escapeHtml(formatWage(employee.wage))}</span>
        </div>
        <div class="popup-row">
          <span class="popup-label">Hire Date</span>
          <span class="popup-value">${escapeHtml(formatDate(employee.createdDate || employee.hireDate))}</span>
        </div>

        <div class="app-password-section">
          <div class="app-password-header">
            <svg class="app-password-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            <span>Portal Access</span>
          </div>

          ${state.credError ? `
            <div class="app-password-error">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              ${escapeHtml(state.credError)}
            </div>
          ` : ''}

          ${state.generatedCreds ? `
            <div class="app-password-generated">
              <div class="generated-badge">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                Credentials Generated
              </div>
              <div class="credential-card">
                <div class="credential-field">
                  <span class="credential-label">Username</span>
                  <div class="credential-value-wrapper">
                    <code class="credential-value">${escapeHtml(state.generatedCreds.username)}</code>
                    <button class="copy-btn" data-action="copy" data-value="${escapeHtml(state.generatedCreds.username)}" title="Copy username">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                      </svg>
                    </button>
                  </div>
                </div>
                <div class="credential-field">
                  <span class="credential-label">Password</span>
                  <div class="credential-value-wrapper">
                    <code class="credential-value credential-password">${escapeHtml(state.generatedCreds.password)}</code>
                    <button class="copy-btn" data-action="copy" data-value="${escapeHtml(state.generatedCreds.password)}" title="Copy password">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              <div class="credential-warning">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                Save now — password won't be shown again
              </div>
            </div>
          ` : ''}

          ${!state.generatedCreds && creds ? `
            <div class="app-password-existing">
              <div class="existing-credential">
                <span class="existing-label">Username</span>
                <code class="existing-value">${escapeHtml(creds.username)}</code>
              </div>
              <div class="existing-status">
                <span class="status-dot active"></span>
                Active
              </div>
            </div>
          ` : ''}

          ${!state.generatedCreds && !creds ? `
            <div class="app-password-empty">
              <div class="empty-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                  <polyline points="10 17 15 12 10 7"/>
                  <line x1="15" y1="12" x2="3" y2="12"/>
                </svg>
              </div>
              <span>No portal access configured</span>
            </div>
          ` : ''}

          <div class="app-password-actions">
            ${creds ? `
              <button class="app-password-btn btn-reset" data-action="reset-password" data-guid="${escapeHtml(employee.guid)}" ${state.credLoading ? 'disabled' : ''}>
                ${state.credLoading ? `<span class="btn-spinner"></span>Resetting...` : `
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
                  </svg>
                  Reset Password
                `}
              </button>
            ` : `
              <button class="app-password-btn btn-generate" data-action="generate-password" data-guid="${escapeHtml(employee.guid)}" ${state.credLoading ? 'disabled' : ''}>
                ${state.credLoading ? `<span class="btn-spinner"></span>Generating...` : `
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
                  </svg>
                  Generate Access
                `}
              </button>
            `}
          </div>
        </div>
      </div>
    </div>
  `;
}

// Event Handlers
function handleClick(e, instance) {
  const target = e.target.closest('[data-action]');
  if (!target) return;

  const action = target.dataset.action;
  const guid = target.dataset.guid;

  switch (action) {
    case 'toggle-info':
      e.stopPropagation();
      if (instance.state.expandedRow === guid) {
        instance.state.expandedRow = null;
      } else {
        instance.state.expandedRow = guid;
      }
      instance.state.generatedCreds = null;
      instance.state.credError = null;
      render(instance);
      break;

    case 'close-popup':
      instance.state.expandedRow = null;
      instance.state.generatedCreds = null;
      instance.state.credError = null;
      render(instance);
      break;

    case 'clear-search':
      instance.state.searchTerm = '';
      render(instance);
      break;

    case 'sort':
      const column = target.dataset.column;
      if (instance.state.sortColumn === column) {
        instance.state.sortDirection = instance.state.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        instance.state.sortColumn = column;
        instance.state.sortDirection = 'asc';
      }
      render(instance);
      break;

    case 'generate-password':
      const empToGenerate = instance.employees.find(emp => emp.guid === guid);
      if (empToGenerate) generateAppCredentials(instance, empToGenerate);
      break;

    case 'reset-password':
      resetAppPassword(instance, guid);
      break;

    case 'copy':
      const value = target.dataset.value;
      navigator.clipboard.writeText(value);
      break;
  }
}

function handleInput(e, instance) {
  const target = e.target;
  const action = target.dataset.action;

  if (action === 'search') {
    instance.state.searchTerm = target.value;
    render(instance);
  }
}

function handleChange(e, instance) {
  const target = e.target;
  const action = target.dataset.action;

  switch (action) {
    case 'job-filter':
      instance.state.jobFilter = target.value;
      render(instance);
      break;
    case 'status-filter':
      instance.state.statusFilter = target.value;
      render(instance);
      break;
  }
}

export default ToastEmployeesView;
